import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

import { Eye, Calendar, MessageSquare, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { ROUTE_BUILDERS } from '@/config/routes'
import ContractEditor from './edit-minuta'
import { useCurrentUser } from '@/features/auth/state/auth'

export interface EditSuggestion {
  id: string
  requestEdit: boolean
  acceptEdit: boolean | null
  isEdit: boolean
  requestChange: string
  change: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface EditSuggestionsProps {
  suggestions: EditSuggestion[]
  editableHtml: string
  fullStyledHtml: string
  handleSave: (html: string, id_suggestion: string) => Promise<void>
}

export default function EditSuggestionsList({ suggestions, editableHtml, handleSave }: EditSuggestionsProps) {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all')
  const user = useCurrentUser()
  const stats = useMemo(
    () => ({
      total: suggestions.length,
      pending: suggestions.filter((s: EditSuggestion) => s.acceptEdit === null && s.isActive).length,
      approved: suggestions.filter((s: EditSuggestion) => s.acceptEdit === true).length,
      rejected: suggestions.filter((s: EditSuggestion) => s.acceptEdit === false || !s.isActive).length
    }),
    [suggestions]
  )
  const activeEditId = useMemo(() => {
    return suggestions.find(s => s.isActive && s.isEdit)?.id ?? null
  }, [suggestions])
  const filteredSuggestions = useMemo(() => {
    let list = suggestions

    if (statusFilter !== 'all') {
      list = list.filter((s: EditSuggestion) => {
        if (statusFilter === 'pending') return s.acceptEdit === null && s.isActive
        if (statusFilter === 'approved') return s.acceptEdit === true
        if (statusFilter === 'rejected') return s.acceptEdit === false || !s.isActive
        return true
      })
    }

    return list
  }, [suggestions, statusFilter])

  const getStatusConfig = (suggestion: EditSuggestion) => {
    // Rejeitada (explicitamente rejeitada OU inativa)
    if (!suggestion.isActive || suggestion.acceptEdit === false) {
      return {
        badge: (
          <Badge variant="destructive" className="gap-1">
            <XCircle className="w-3 h-3" />
            Rejeitada
          </Badge>
        ),
        color: 'border-red-200 bg-red-50/50'
      }
    }

    // Aprovada
    if (suggestion.acceptEdit === true) {
      return {
        badge: (
          <Badge variant="default" className="gap-1 bg-green-600">
            <CheckCircle className="w-3 h-3" />
            Aprovada
          </Badge>
        ),
        color: 'border-green-200 bg-green-50/50'
      }
    }

    // Em revisão pelo editor (ainda não salvou a alteração final)
    if (suggestion.isActive && suggestion.isEdit === false) {
      return {
        badge: (
          <Badge variant="secondary" className="gap-1 bg-blue-100 text-blue-800 border-blue-200">
            <Clock className="w-3 h-3" />
            Em revisão
          </Badge>
        ),
        color: 'border-blue-200 bg-blue-50/50'
      }
    }

    // Pendente de aprovação (edit foi salvo, ainda sem aceite)
    return {
      badge: (
        <Badge variant="secondary" className="gap-1 bg-amber-100 text-amber-800 border-amber-200">
          <Clock className="w-3 h-3" />
          Pendente
        </Badge>
      ),
      color: 'border-amber-200 bg-amber-50/50'
    }
  }

  const handleViewPreviewPage = (editId: string) => {
    navigate(ROUTE_BUILDERS.editPreview(editId))
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto p-6 max-w-7xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-900">Sugestões de Edição</h1>
            <p className="text-slate-600 mt-1">Gerencie as solicitações de alteração do contrato</p>
          </div>
        </div>

        {/* Barra de filtros (sticky) */}

        <Card className="border-0 shadow-sm bg-white/80 backdrop-blur-sm">
          <CardContent className="pt-0">
            <div className="flex flex-col gap-3">
              {/* Tabs de status */}
              <Tabs value={statusFilter} onValueChange={v => setStatusFilter(v as any)} className="w-full">
                <TabsList className="grid grid-cols-4 w-full">
                  <TabsTrigger value="all">
                    Todos{' '}
                    <Badge variant="secondary" className="ml-2">
                      {stats.total}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger value="pending">
                    Pendentes{' '}
                    <Badge variant="secondary" className="ml-2">
                      {stats.pending}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger value="approved">
                    Aprovadas{' '}
                    <Badge variant="secondary" className="ml-2">
                      {stats.approved}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger value="rejected">
                    Rejeitadas{' '}
                    <Badge variant="secondary" className="ml-2">
                      {stats.rejected}
                    </Badge>
                  </TabsTrigger>
                </TabsList>

                {/* Só usamos um conteúdo com a lista filtrada; o valor da tab controla o filtro */}
                <TabsContent value={statusFilter} className="mt-3" />
              </Tabs>
            </div>

            {/* Área rolável de resultados */}
            <div
              className={cn(
                'mt-4 rounded-xl border border-slate-200/60 bg-white/70 backdrop-blur-sm',
                // altura dinâmica: ocupa a tela menos o header + filtros (~260–320px). Ajuste se precisar.
                'max-h-[calc(100vh-260px)] overflow-y-auto'
              )}
            >
              <div className="p-4 space-y-4">
                {filteredSuggestions.length === 0 ? (
                  <Card className="border-0 shadow-sm bg-white/80 backdrop-blur-sm">
                    <CardContent className="text-center py-16">
                      <div className="flex flex-col items-center gap-4">
                        <div className="p-4 bg-slate-100 rounded-full">
                          <MessageSquare className="w-8 h-8 text-slate-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-2">
                            {suggestions.length === 0 ? 'Nenhuma sugestão encontrada' : 'Nenhum resultado'}
                          </h3>
                          <p className="text-slate-600">
                            {suggestions.length === 0
                              ? 'Ainda não há sugestões de edição para este contrato.'
                              : 'Tente ajustar os filtros para encontrar o que procura.'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  filteredSuggestions.map((suggestion: EditSuggestion) => {
                    const statusConfig = getStatusConfig(suggestion)
                    return (
                      <Card
                        key={suggestion.id}
                        className={cn(
                          'border-0 shadow-sm bg-white/80 backdrop-blur-sm hover:shadow-md transition-all duration-200',
                          statusConfig?.color ?? ''
                        )}
                      >
                        <CardContent className="p-6">
                          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                            <div className="flex-1 space-y-4">
                              {/* Status e Data */}
                              <div className="flex flex-wrap items-center gap-3">
                                {statusConfig?.badge}
                                <Separator orientation="vertical" className="h-4" />
                                <span className="text-sm text-slate-500 flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {new Date(suggestion.createdAt).toLocaleString('pt-BR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>

                              {/* Solicitação */}
                              <div>
                                <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                                  <AlertCircle className="w-4 h-4 text-blue-600" />
                                  Solicitação
                                </h3>
                                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                                  <p className="text-slate-700 leading-relaxed">{suggestion.requestChange}</p>
                                </div>
                              </div>

                              {/* Alterações aplicadas */}
                              {suggestion.isEdit && suggestion.change && (
                                <div>
                                  <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                    Alteração Aplicada
                                  </h3>
                                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <p className="text-green-800 leading-relaxed">{suggestion.change}</p>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Ações */}
                            {suggestion.isActive && suggestion.isEdit === false && (
                              <div className="flex flex-col gap-2 lg:min-w-[140px]">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewPreviewPage(suggestion.id)}
                                  className="flex items-center gap-2 justify-center hover:bg-blue-50 hover:border-blue-300"
                                >
                                  <Eye className="w-4 h-4" />
                                  Ver Alterações
                                </Button>
                              </div>
                            )}
                          </div>
                          {suggestion.id === activeEditId &&
                            (user?.typeCargo === 'CEO' || user?.typeCargo === 'JURIDICO') && (
                              <div className="relative z-10 mt-10">
                                <ContractEditor
                                  initialHtml={editableHtml}
                                  onSave={handleSave}
                                  id_suggestion={suggestion.id}
                                />
                              </div>
                            )}
                        </CardContent>
                      </Card>
                    )
                  })
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
